import { Badge, Box, Button, HStack, Stack, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FiAward, FiExternalLink, FiFileText } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  SourcingEventStatus,
  SourcingEventV2Dto,
  useConfirmAwardMutation,
  useGetEvaluationQuery,
  useListPublicBidsForEventQuery,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useDisplayDate } from "../../../../common/formatting/useDisplayDate";
import { urls } from "../../../../urls";
import { AwardBidsTable } from "./AwardBidsTable";
import { AwardLetterWorkbench } from "./AwardLetterWorkbench";
import { CreateContractFromBidModal } from "./CreateContractFromBidModal";

export const AwardOutcome = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const apiError = useApiError();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const { data } = useListPublicBidsForEventQuery({ eventId: event.id });
  const { data: evaluation, isError: isEvaluationError } = useGetEvaluationQuery({ eventId: event.id });
  const [confirm, { isLoading: isConfirming }] = useConfirmAwardMutation();
  const modal = useDisclosure();

  const award = event.award;
  const bids = data?.bids ?? [];
  const winningBidId = award?.winningBidId ?? undefined;
  const winner = bids.find((b) => b.id === winningBidId);
  const winnerDocuments = winner
    ? [
        ...(winner.documents ?? []),
        ...((winner.qualificationResponses ?? []).flatMap((response) => response.evidenceDocuments ?? [])),
      ].filter((document, index, documents) => documents.findIndex((d) => d.id === document.id) === index)
    : [];
  const contractId = award?.contractIds?.[0];
  const isDecision = event.status === SourcingEventStatus.AwardDecision;

  const standstillExpiresAt = award?.standstillExpiresAt ?? undefined;
  const standstillDispatched = standstillExpiresAt !== undefined;
  const standstillExpiry = standstillExpiresAt ? new Date(standstillExpiresAt) : undefined;

  // Re-render exactly when the standstill deadline passes so the Confirm button enables itself without
  // needing a manual refresh; Date.now() alone only re-evaluates on an unrelated render.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (standstillExpiry === undefined) return;
    const msUntilExpiry = standstillExpiry.getTime() - Date.now();
    if (msUntilExpiry <= 0) return;
    const timer = setTimeout(() => setNow(Date.now()), msUntilExpiry);
    return () => clearTimeout(timer);
  }, [standstillExpiry]);

  const standstillElapsed = standstillExpiry !== undefined && now >= standstillExpiry.getTime();
  const standstillDaysRemaining =
    standstillExpiry !== undefined
      ? Math.max(0, Math.ceil((standstillExpiry.getTime() - now) / (1000 * 60 * 60 * 24)))
      : 0;

  const onConfirm = async () => {
    try {
      await confirm({ eventId: event.id }).unwrap();
      toast({ title: t("Award confirmed"), status: "success", position: "top-right" });
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Stack spacing="6">
      <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="6">
        <HStack spacing="3" mb="1">
          <FiAward color="var(--chakra-colors-smPrimary)" />
          <Text fontWeight="semibold" fontSize="lg" color="smText">
            {winner?.ownerName || t("Unknown")}
          </Text>
          <Badge colorScheme="teal">{t("Winner")}</Badge>
        </HStack>
        {award?.internalNote && (
          <Box mt="3">
            <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase">
              {t("Internal note")}
            </Text>
            <Text color="smText" whiteSpace="pre-wrap">
              {award.internalNote}
            </Text>
          </Box>
        )}
      </Box>

      {isEvaluationError ? (
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="4">
          <Text fontSize="sm" color="smError">
            {t("Could not load the evaluation.")}
          </Text>
        </Box>
      ) : (
        evaluation &&
        evaluation.bids.length > 0 && (
          <AwardBidsTable evaluation={evaluation} mode="confirmed" selectedBidId={winningBidId} />
        )
      )}

      <AwardLetterWorkbench event={event} />

      {isDecision ? (
        <Box bg="smBackgroundSecondary" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
          {!standstillDispatched ? (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("Send the award letters to start the standstill period before confirming the award.")}
            </Text>
          ) : standstillElapsed ? (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("The standstill period has passed. You can now confirm the award.")}
            </Text>
          ) : (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("Standstill period ends {{date}} ({{count}} days remaining). The award can be confirmed after this.", {
                date: standstillExpiresAt ? displayDate(standstillExpiresAt) : "",
                count: standstillDaysRemaining,
              })}
            </Text>
          )}
          <Button
            colorScheme="teal"
            onClick={onConfirm}
            isLoading={isConfirming}
            isDisabled={!standstillElapsed}
          >
            {t("Confirm award")}
          </Button>
        </Box>
      ) : (
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
          <HStack justify="space-between">
            <HStack spacing="3">
              <FiFileText />
              <Text color="smText">{t("Contract")}</Text>
            </HStack>
            {contractId ? (
              <Button
                variant="outline"
                colorScheme="teal"
                rightIcon={<FiExternalLink />}
                onClick={() => navigate(urls.contracts.edit.go(contractId, event.id).details)}
              >
                {t("Go to contract")}
              </Button>
            ) : (
              <Button colorScheme="teal" onClick={modal.onOpen} isDisabled={!winner}>
                {t("Create contract")}
              </Button>
            )}
          </HStack>
        </Box>
      )}

      {winner && (
        <CreateContractFromBidModal
          eventId={event.id}
          bidId={winner.id}
          supplierName={winner.ownerName}
          defaultTitle={event.eventData.title}
          documents={winnerDocuments}
          isOpen={modal.isOpen}
          onClose={modal.onClose}
        />
      )}
    </Stack>
  );
};
